import { cached } from '@glimmer/tracking';

import type { DSData } from 'webapp-types';

import DsDataOutputBase from 'webapp/models-custom/ds-data/output/base';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import DsDataPointOther from 'webapp/models-custom/ds-data/point/other';
import type DsDataRootCompare from 'webapp/models-custom/ds-data/root/compare';
import type DsDataRootTopLevel from 'webapp/models-custom/ds-data/root/top';

export default class DsDataOutputOtherLevel extends DsDataOutputBase {
	dataRoot: DsDataRootTopLevel | DsDataRootCompare;
	points: DsDataPointOther[];

	ts: number;
	count: number;

	constructor(dataRoot: DsDataRootTopLevel | DsDataRootCompare, rawData: DSData.PointGroupOther) {
		super(rawData);
		this.dataRoot = dataRoot;
		this.count = rawData.count;
		this.ts = rawData.ts || 0;
		this.points = (rawData.items || []).map(
			(pointData) => new DsDataPointOther(this, pointData),
		);
	}

	get granulationLabel() {
		const granulationLabels = this.dataRoot.granulationLabels;
		return granulationLabels.find((item) => item.ts === this.ts)!;
	}

	override get titleWithoutMetric() {
		let title = super.titleWithoutMetric || '';
		if (title) {
			title = '/ ' + title;
		}
		return 'Other ' + title;
	}

	override get titleWithoutUnit() {
		return this.metricName + ' / Other';
	}

	@cached
	get index() {
		const granulationLabel = this.granulationLabel;
		return this.dataRoot.granulationLabels.indexOf(granulationLabel);
	}

	// used when we know this is a number so we don't look at timestamps
	get pointTotal(): DsDataPointOther | null {
		return this.points.find((item) => item.type === 'total') || null;
	}

	get metricSelectionData(): MetricSelectionData {
		return {
			dsId: this.dataRoot.dataSelect.numericId!,
			primaryDsId: this.dataRoot.dataSelect.updateFrom,
			metricName: this.dataRoot.metricName,
			dimensionValues: this.dimensionValues,
			unit: this.unit,
			metricSetupData: this.dataRoot.dataSelect.metricSetupData,
		};
	}

	@cached
	get compareOutputs() {
		// only if this is top level other
		const outputs: DsDataOutputOtherLevel[] = [];
		if (this.dataRoot.isTop) {
			(this.dataRoot as DsDataRootTopLevel).compareRoots.forEach((compareRoot) => {
				compareRoot.outputsOther.forEach((compareOutputOther) => {
					// only compare unit and index, as "others" are returned for attributes only atm
					if (
						compareOutputOther.unit === this.unit &&
						compareOutputOther.index === this.index
					) {
						outputs.push(compareOutputOther);
					}
				});
			});
		}
		return outputs;
	}

	@cached
	get topOutputs() {
		// only if this is compare level other
		const outputs: DsDataOutputOtherLevel[] = [];
		if (!this.dataRoot.isTop) {
			const topRoot = this.dataRoot.dataSelect.dataRoot;
			if (topRoot) {
				outputs.push(...topRoot.outputsOther);
			}
		}
		return outputs;
	}

	isMatchForOutput(output: DsDataOutputBase): boolean {
		const root = this.dataRoot;
		if (root.isDiffAttributes) {
			// if root is diff attributes, then this other needs to match unit
			return this.unit === output.unit;
		} else if (root.isDiffUnits) {
			// currently, user always selects exactly one or all units, so we do not support "other" in unit context
			return this.equalDimensionValues(output.dimensionValues);
		} else {
			// just one "other"
			return true;
		}
	}
}
